import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { useMessagingStore } from '../hooks/useMessagingStore.tsx';
import { messages } from '../localization/messages.ts';
import { TurnRail } from './TurnRail.tsx';
import { StageDock, type StageDockProps } from './StageDock.tsx';
import { StageConversationLog } from './StageConversationLog.tsx';
import { StageActivity, StageActivityTrigger } from './StageActivity.tsx';
import { useStageViewport } from './use-keyboard-lift.ts';
import { activitySurfaceNavigationTargets, activitySurfaceTurnIndex } from './activity-context.ts';
import { formatNarrationLine } from './narration-line.ts';
import { ComposerDraftContext } from './ComposerDraftContext.tsx';
import type { StageController } from './useStageController.ts';
import './stage-effects.css';

interface StageLayoutProps {
  stage: StageController;
  header: ReactNode;
  placeholder?: MessageDescriptor;
  preChatNotice?: MessageDescriptor;
  postInputNote?: MessageDescriptor;
  errorMessage?: MessageDescriptor;
  emailAddress?: string;
  advisorLabel?: MessageDescriptor;
  welcomeEyebrow?: MessageDescriptor;
  starterPrompts?: readonly MessageDescriptor[];
  canvasNotice?: ReactNode;
  workspaceRef?: Ref<HTMLDivElement>;
  renderDock?: (props: StageDockProps) => ReactNode;
  children: ReactNode;
}

/** Shared conversation log, history and persistent composer. */
export const StageLayout = observer(function StageLayout({
  stage,
  header,
  placeholder,
  preChatNotice,
  postInputNote,
  errorMessage,
  emailAddress,
  advisorLabel,
  welcomeEyebrow,
  starterPrompts,
  canvasNotice,
  workspaceRef,
  renderDock,
  children,
}: StageLayoutProps) {
  const intl = useIntl();
  const store = useMessagingStore();
  const viewport = useStageViewport();
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const activityTriggerRef = useRef<HTMLButtonElement>(null);
  const wasNearBottom = useRef(true);
  const [dockHeight, setDockHeight] = useState(0);
  const {
    turns,
    surfaceEntries,
    loadingLine,
    errorNotice,
    currentRailIndex,
    activityContext,
    viewingHistory,
    activityStatus,
    activityOpen,
    setActivityOpen,
    setSelectedIndex,
    dismissedErrorRunId,
    setDismissedErrorRunId,
    pendingRequest,
    runInFlight,
    voiceActive,
    dockNarration,
  } = stage;

  const pageKey = stage.view.key;
  const visiblePageIndex = viewingHistory ? currentRailIndex : turns.length - 1;
  const visibleTurnCount = Math.max(0, Math.min(turns.length, visiblePageIndex + 1));
  const hasVisitorMessage = turns.some((turn) => !turn.home) || pendingRequest !== null;
  const showPreChat = !hasVisitorMessage;
  const isWelcome =
    showPreChat && !viewingHistory && turns.some((turn) => turn.home === true);
  const advisorName = advisorLabel ? intl.formatMessage(advisorLabel) : 'AI advisor';
  const eyebrow = welcomeEyebrow ? intl.formatMessage(welcomeEyebrow) : '';
  const promptText = (starterPrompts ?? []).map((prompt) => intl.formatMessage(prompt));
  const formattedPreChatNotice =
    showPreChat && preChatNotice
      ? intl.formatMessage(preChatNotice, {
          privacyPolicy: (chunks) => (
            <a
              href="https://agentplace.io/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="jfy-notice-link"
            >
              {chunks}
            </a>
          ),
        })
      : undefined;
  const formattedPostInputNote =
    showPreChat && postInputNote ? intl.formatMessage(postInputNote) : undefined;
  const hasUndismissedError =
    Boolean(errorNotice) && store.lastRunError?.responseId !== dismissedErrorRunId;
  const formattedError =
    hasUndismissedError && errorMessage && emailAddress
      ? intl.formatMessage(errorMessage, {
          email: (
            <a className="jfy-error-link" href={`mailto:${emailAddress}`}>
              {emailAddress}
            </a>
          ),
        })
      : errorNotice;
  const errorContent = hasUndismissedError ? (
    <div className="jfy-error-content">
      <span>{formattedError}</span>
      <button
        type="button"
        aria-label={intl.formatMessage(messages.dismiss)}
        onClick={() => setDismissedErrorRunId(store.lastRunError?.responseId ?? null)}
        className="jfy-error-dismiss"
      >
        &times;
      </button>
    </div>
  ) : undefined;

  useEffect(() => {
    const canvas = canvasScrollRef.current;
    if (!canvas) {
      return;
    }
    const measure = () => {
      if (getComputedStyle(canvas).overflowY === 'visible') {
        wasNearBottom.current =
          window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 140;
      } else {
        wasNearBottom.current =
          canvas.scrollHeight - canvas.scrollTop - canvas.clientHeight < 140;
      }
    };
    canvas.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      canvas.removeEventListener('scroll', measure);
      window.removeEventListener('scroll', measure);
    };
  }, []);

  // New conversation turns should reveal their latest message unless the visitor has scrolled up.
  useEffect(() => {
    const canvas = canvasScrollRef.current;
    if (!canvas || visibleTurnCount <= 1) {
      return;
    }
    if (viewingHistory || pendingRequest !== null || wasNearBottom.current) {
      if (getComputedStyle(canvas).overflowY === 'visible') {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'instant' });
      } else {
        canvas.scrollTo({ top: canvas.scrollHeight, behavior: 'instant' });
      }
      wasNearBottom.current = true;
    }
  }, [pageKey, visibleTurnCount, pendingRequest, viewingHistory, turns.at(-1)?.responsePreview]);

  const dockProps: StageDockProps = {
    onHeightChange: setDockHeight,
    ready: stage.ready,
    activity: (
      <StageActivityTrigger
        status={activityStatus}
        detail={
          runInFlight && pendingRequest === null
            ? formatNarrationLine(store.narration.detail, intl)
            : undefined
        }
        working={
          runInFlight || loadingLine?.kind === 'connecting' || loadingLine?.kind === 'preparing'
        }
        open={activityOpen && !viewport.compact}
        available={
          Boolean(activityContext.responseId) || store.narration.runs.size > 0 || activityOpen
        }
        triggerRef={activityTriggerRef}
        onToggle={() => setActivityOpen((open) => viewport.compact || !open)}
      />
    ),
    voiceActive,
    placeholder: placeholder ? intl.formatMessage(placeholder) : undefined,
    preChatNotice: formattedPreChatNotice,
    postInputNote: formattedPostInputNote,
    errorNotice: errorContent,
    narration: voiceActive ? dockNarration.text : activityStatus,
    narrationVariant: dockNarration.variant,
    isUserRequestPending: runInFlight,
    onSend: stage.sendMessage,
    onStopStreaming: store.stopStreaming,
  };

  return (
    <ComposerDraftContext.Provider value={stage.composerDraft}>
      <div
        className="stage relative bg-background text-foreground"
        data-activity-open={activityOpen}
        data-keyboard-compact={viewport.compact}
        data-pre-chat={showPreChat}
        style={viewport.style}
      >
        {children}
        <div ref={workspaceRef} className="stage-workspace relative min-h-0 min-w-0">
          <TurnRail turns={turns} currentIndex={currentRailIndex} onSelect={stage.selectTurn} />
          <div ref={canvasScrollRef} className="stage-canvas h-full overflow-y-auto overflow-x-hidden">
            {canvasNotice}
            {header}
            <div className="relative flex-1">
              <div
                className="stage-page pb-48 pt-8"
                data-stage-page
                style={
                  dockHeight
                    ? {
                        paddingBottom: `calc(${dockHeight}px + 1.5rem + var(--stage-mobile-bottom-clearance, 0px))`,
                      }
                    : undefined
                }
              >
                <div className="jfy-chat-panel mx-auto w-full">
                  <StageConversationLog
                    turns={turns}
                    visibleTurnCount={visibleTurnCount}
                    displayedTurnIndex={visiblePageIndex}
                    view={stage.view}
                    loadingLine={loadingLine}
                    surfaceEntries={surfaceEntries}
                    viewingHistory={viewingHistory}
                    advisorLabel={advisorName}
                    welcomeEyebrow={eyebrow}
                    starterPrompts={promptText}
                    showStarterPrompts={isWelcome}
                    ready={stage.ready}
                    onSubmit={() => stage.followFromCurrentView()}
                    onStarterPrompt={(text) => stage.sendMessage(text)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <StageActivity
          open={activityOpen}
          responseId={activityContext.responseId}
          request={activityContext.request}
          turns={turns}
          sharedResponse={activityContext.sharedResponse}
          viewingHistory={viewingHistory}
          liveStatus={
            runInFlight ? activityStatus || intl.formatMessage(messages.working) : undefined
          }
          triggerRef={activityTriggerRef}
          onClose={() => setActivityOpen(false)}
          onViewLive={() => setSelectedIndex(null)}
          navigableSurfaceIds={activitySurfaceNavigationTargets(
            turns,
            surfaceEntries.map(([id]) => id),
            stage.view.kind === 'surface' ? stage.view.surfaceId : null,
          )}
          onViewSurface={(surfaceId) => {
            const index = activitySurfaceTurnIndex(turns, surfaceId);
            if (index >= 0) {
              stage.selectTurn(index);
            }
          }}
        />
        {renderDock ? renderDock(dockProps) : <StageDock {...dockProps} />}
      </div>
    </ComposerDraftContext.Provider>
  );
});
